// src/components/settings/SettingsPanel.tsx
import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  X, Bell, CreditCard, Mail, AlertCircle, CheckCircle,
  Zap, Crown, LogOut, ChevronRight
} from 'lucide-react'
import { toast } from 'react-hot-toast'
import './Settings.css'

// ✅ FIXED: Use environment variable for API URL
const API_URL = import.meta.env.VITE_API_URL || 'https://emailai-backend-658e.onrender.com/api'

interface UserProfile {
  _id?: string
  id?: string
  name: string
  email: string
  plan?: string
  selectedPlan?: string
  billingCycle?: 'monthly' | 'yearly'
  paymentStatus?: string
  paymentDate?: string
  isFreeUser?: boolean
}

interface SettingsPanelProps {
  isOpen: boolean
  onClose: () => void
  user: UserProfile | null
  onLogout: () => void
  onUserUpdate?: (updatedUser: UserProfile) => void
}

interface NotificationPreferences {
  campaignPerformance: boolean
  weeklyDigest: boolean
  upgradeReminders: boolean
  productUpdates: boolean
  weeklyTips: boolean
}

interface NotificationState {
  type: 'success' | 'error'
  message: string
}

const PLAN_DETAILS: Record<string, { name: string; monthlyPrice: number; yearlyPrice: number; features: string }> = {
  starter: { name: 'Starter', monthlyPrice: 29, yearlyPrice: 19, features: 'Up to 10,000 subscribers • Basic AI • 5 templates • 1 team member' },
  pro: { name: 'Professional', monthlyPrice: 99, yearlyPrice: 69, features: 'Up to 50,000 subscribers • Advanced AI • 20 templates • 5 team members • A/B testing' },
  professional: { name: 'Professional', monthlyPrice: 99, yearlyPrice: 69, features: 'Up to 50,000 subscribers • Advanced AI • 20 templates • 5 team members • A/B testing' },
  enterprise: { name: 'Enterprise', monthlyPrice: 299, yearlyPrice: 199, features: 'Unlimited subscribers • Advanced AI • Unlimited templates • 24/7 support • SLA' },
  premium: { name: 'Enterprise', monthlyPrice: 299, yearlyPrice: 199, features: 'Unlimited subscribers • Advanced AI • Unlimited templates • 24/7 support • SLA' }
}

export default function SettingsPanel({ isOpen, onClose, user, onLogout, onUserUpdate }: SettingsPanelProps) {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'notifications' | 'billing'>('notifications')
  const [notification, setNotification] = useState<NotificationState | null>(null)
  const [isLoadingPreferences, setIsLoadingPreferences] = useState(false)
  const [billingHistory, setBillingHistory] = useState<any[]>([])
  const [isLoadingBilling, setIsLoadingBilling] = useState(false)
  const [csrfToken, setCsrfToken] = useState<string>('')

  const [notifications, setNotifications] = useState<NotificationPreferences>({
    campaignPerformance: true,
    weeklyDigest: true,
    upgradeReminders: true,
    productUpdates: false,
    weeklyTips: true
  })

  // Fetch CSRF token on component mount
  useEffect(() => {
    const fetchCsrfToken = async () => {
      try {
        const response = await fetch(`${API_URL}/csrf-token`, {
          credentials: 'include'
        })
        const data = await response.json()
        setCsrfToken(data.csrfToken)
      } catch (error) {
        console.error('Failed to fetch CSRF token:', error)
      }
    }
    
    if (isOpen) {
      fetchCsrfToken()
    }
  }, [isOpen])

  const getPlanDisplayName = (plan: string | undefined) => {
    if (!plan) return 'Starter'
    const planKey = plan.toLowerCase()
    return PLAN_DETAILS[planKey]?.name || plan.charAt(0).toUpperCase() + plan.slice(1)
  }

  const getCurrentPrice = () => {
    const planKey = (user?.plan || user?.selectedPlan || 'starter').toLowerCase()
    const plan = PLAN_DETAILS[planKey] || PLAN_DETAILS.starter
    return user?.billingCycle === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice
  }

  const getYearlySavings = () => {
    const planKey = (user?.plan || user?.selectedPlan || 'starter').toLowerCase()
    const plan = PLAN_DETAILS[planKey] || PLAN_DETAILS.starter
    return (plan.monthlyPrice * 12) - (plan.yearlyPrice * 12)
  }

  const getSavingsPercent = () => {
    const planKey = (user?.plan || user?.selectedPlan || 'starter').toLowerCase()
    const plan = PLAN_DETAILS[planKey] || PLAN_DETAILS.starter
    return Math.round((1 - plan.yearlyPrice / plan.monthlyPrice) * 100)
  }

  const getPlanFeatures = () => {
    const planKey = (user?.plan || user?.selectedPlan || 'starter').toLowerCase()
    const plan = PLAN_DETAILS[planKey] || PLAN_DETAILS.starter
    return plan.features
  }

  const showNotification = useCallback((type: 'success' | 'error', message: string) => {
    setNotification({ type, message })
    setTimeout(() => setNotification(null), 3000)
  }, [])

  const fetchNotificationPreferences = async () => {
    setIsLoadingPreferences(true)
    try {
      const token = localStorage.getItem('accessToken')
      
      const response = await fetch(`${API_URL}/user/notification-preferences`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'X-CSRF-Token': csrfToken
        },
        credentials: 'include'
      })
      if (response.ok) {
        const data = await response.json()
        if (data.preferences) setNotifications(data.preferences)
      }
    } catch (error) {
      console.error('Error fetching notification preferences:', error)
    } finally {
      setIsLoadingPreferences(false)
    }
  }

  const fetchBillingHistory = async () => {
    setIsLoadingBilling(true)
    try {
      const token = localStorage.getItem('accessToken')
      
      const response = await fetch(`${API_URL}/payments/history?email=${user?.email}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'X-CSRF-Token': csrfToken
        },
        credentials: 'include'
      })
      if (response.ok) {
        const data = await response.json()
        setBillingHistory(data.invoices || [])
      }
    } catch (error) {
      console.error('Error fetching billing history:', error)
    } finally {
      setIsLoadingBilling(false)
    }
  }

  useEffect(() => {
    if (isOpen && activeTab === 'notifications') fetchNotificationPreferences()
    if (isOpen && activeTab === 'billing' && user?.email) fetchBillingHistory()
  }, [isOpen, activeTab, user?.email, csrfToken])

  const handleNotificationChange = async (key: keyof NotificationPreferences) => {
    const newValue = !notifications[key]
    setNotifications(prev => ({ ...prev, [key]: newValue }))
    try {
      const token = localStorage.getItem('accessToken')
      
      const response = await fetch(`${API_URL}/user/notification-preferences`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`, 
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        credentials: 'include',
        body: JSON.stringify({ ...notifications, [key]: newValue })
      })
      if (!response.ok) {
        setNotifications(prev => ({ ...prev, [key]: !newValue }))
        showNotification('error', 'Failed to update preference')
      } else {
        toast.success(`${key.replace(/([A-Z])/g, ' $1').toLowerCase()} ${newValue ? 'enabled' : 'disabled'}`)
      }
    } catch (error) {
      setNotifications(prev => ({ ...prev, [key]: !newValue }))
      showNotification('error', 'Failed to update preference')
    }
  }

  const handleUpgradePlan = () => {
    navigate('/plan-selection', {
      state: { 
        from: 'settings', 
        currentPlan: user?.plan || user?.selectedPlan, 
        email: user?.email, 
        billingCycle: user?.billingCycle 
      }
    })
    onClose()
  }

  const handleSwitchToYearly = async () => {
    try {
      const token = localStorage.getItem('accessToken')
      
      const response = await fetch(`${API_URL}/plans/switch-billing`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`, 
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        credentials: 'include',
        body: JSON.stringify({ email: user?.email, billingCycle: 'yearly' })
      })
      if (response.ok) {
        toast.success('Switched to yearly billing! Savings applied.')
        if (onUserUpdate) onUserUpdate({ ...user!, billingCycle: 'yearly' })
      } else {
        const error = await response.json()
        toast.error(error.message || 'Failed to switch billing cycle')
      }
    } catch (error) {
      toast.error('Network error')
    }
  }

  // ❌ REMOVED: handleCancelSubscription function

  if (!isOpen) return null

  return (
    <>
      {/* Overlay */}
      <div className={`modal-overlay ${isOpen ? 'visible' : ''}`} onClick={onClose} />
      
      {/* Panel - slides from right */}
      <div className={`settings-panel-modal ${isOpen ? 'open' : ''}`}>
        <button className="modal-close" onClick={onClose}>
          <X className="w-5 h-5" />
        </button>
        
        <div className="modal-content">
          {/* Notification Toast */}
          {notification && (
            <div className={`settings-toast ${notification.type}`}>
              {notification.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
              <span>{notification.message}</span>
            </div>
          )}

          {/* Header */}
          <div className="settings-panel-header">
            <h2>Account Settings</h2>
          </div>

          {/* Tabs */}
          <div className="settings-panel-tabs">
            {[{ id: 'notifications' as const, label: 'Notifications', icon: Bell }, { id: 'billing' as const, label: 'Billing & Plans', icon: CreditCard }].map(tab => {
              const Icon = tab.icon
              return (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}>
                  <Icon className="w-4 h-4" />{tab.label}
                </button>
              )
            })}
          </div>

          {/* Content */}
          <div className="settings-panel-content">

            {/* NOTIFICATIONS TAB */}
            {activeTab === 'notifications' && (
              <div className="notifications-section">
                <p className="section-desc">Manage how you receive notifications about your account and campaigns.</p>

                {isLoadingPreferences ? (
                  <div className="loading-state"><div className="spinner" /><p>Loading preferences...</p></div>
                ) : (
                  <>
                    {[
                      { key: 'campaignPerformance' as const, title: 'Campaign Performance', description: 'Get notified when your campaigns receive new views and subscribers' },
                      { key: 'weeklyDigest' as const, title: 'Weekly Digest', description: 'Receive a summary of your campaign stats every Monday' },
                      { key: 'weeklyTips' as const, title: 'Weekly Tips', description: 'Get helpful email marketing tips and best practices' },
                      { key: 'upgradeReminders' as const, title: 'Upgrade Reminders', description: 'Notifications about plan upgrades and new features' },
                      { key: 'productUpdates' as const, title: 'Product Updates', description: 'Stay informed about new features and platform changes' }
                    ].map(item => (
                      <div key={item.key} className="notification-item">
                        <div className="notification-info">
                          <Mail className="w-5 h-5" />
                          <div><p className="notification-title">{item.title}</p><p className="notification-desc">{item.description}</p></div>
                        </div>
                        <button onClick={() => handleNotificationChange(item.key)} className={`toggle ${notifications[item.key] ? 'active' : ''}`}>
                          <span className="toggle-slider" />
                        </button>
                      </div>
                    ))}
                    <div className="tip-card">
                      <p>💡 <strong>Tip:</strong> You can always manage these settings later from your account page.</p>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* BILLING TAB */}
            {activeTab === 'billing' && (
              <div className="billing-section">
                <div className="current-plan-card">
                  <div className="plan-header">
                    <div>
                      <div className="plan-name">
                        <h3>{getPlanDisplayName(user?.plan)} Plan</h3>
                        {(user?.plan === 'pro' || user?.plan === 'professional') && <Crown className="w-5 h-5" />}
                      </div>
                      <p className="billing-cycle">{user?.billingCycle === 'yearly' ? 'Billed annually' : 'Billed monthly'}</p>
                    </div>
                    <div className="plan-price">
                      <span className="price">${getCurrentPrice()}/mo</span>
                      {user?.billingCycle === 'yearly' && <span className="savings">Save {getSavingsPercent()}% (${getYearlySavings()}/year)</span>}
                    </div>
                  </div>
                  <p className="plan-features">{getPlanFeatures()}</p>
                  <div className="plan-actions">
                    <button onClick={handleUpgradePlan} className="upgrade-btn"><Zap className="w-4 h-4" />Change Plan</button>
                    {user?.plan !== 'enterprise' && user?.billingCycle === 'monthly' && (
                      <button onClick={handleSwitchToYearly} className="yearly-btn">Switch to Yearly (Save 30%)</button>
                    )}
                  </div>
                </div>

                <div className="payment-method-card">
                  <h4>Payment Method</h4>
                  <div className="payment-info">
                    <div className="payment-icon"><CreditCard className="w-5 h-5" /></div>
                    <div><p>{user?.paymentStatus === 'completed' ? 'PayPal' : 'No payment method'}</p><span>{user?.paymentDate ? `Last payment: ${new Date(user.paymentDate).toLocaleDateString()}` : 'No payments yet'}</span></div>
                  </div>
                </div>

                <div className="billing-history">
                  <h4>Billing History</h4>
                  {isLoadingBilling ? <div className="loading-state"><div className="spinner" /></div> :
                    billingHistory.length > 0 ? (
                      <div className="history-list">
                        {billingHistory.map((invoice, i) => (
                          <div key={i} className="history-item">
                            <div><p>{invoice.description}</p><span>{invoice.date}</span></div>
                            <div className="history-amount"><p>${invoice.amount}</p><span className={invoice.status}>{invoice.status === 'paid' ? '✓ Paid' : 'Pending'}</span></div>
                          </div>
                        ))}
                      </div>
                    ) : <div className="empty-state"><p>No billing history available</p></div>
                  }
                </div>

                {/* ❌ REMOVED: Cancel Subscription Card */}

                <div className="paypal-note"><p>⚠️ <strong>Note:</strong> Billing is managed securely through PayPal.</p></div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="settings-panel-footer">
            <button onClick={onLogout} className="logout-btn"><LogOut className="w-4 h-4" />Logout</button>
          </div>
        </div>
      </div>
    </>
  )
}